/**
 * Consommation du buffer de tap de notification push posé par le script
 * inline d'index.html (voir le commentaire là-bas : le natif ne délivre
 * l'additionalData qu'UNE seule fois, potentiellement avant que le bundle
 * React n'ait enregistré son listener).
 *
 * Deux consommateurs, un seul gagnant (le buffer est effacé à la lecture) :
 * - main.tsx au boot : réécrit l'URL AVANT le montage du router pour
 *   démarrer directement sur la cible (zéro flash Home, zéro reload) ;
 * - initPushClickHandler (medianNativeBridge) : rattrape un payload arrivé
 *   entre l'exécution de main.tsx et l'enregistrement du listener.
 */

interface BufferedPush {
  data: unknown;
  at: number;
}

const STORAGE_KEY = 'koken_pending_push';

/**
 * Au-delà de cet âge, un payload bufferisé est considéré périmé et ignoré :
 * garde-fou contre le rejeu d'un vieux tap au boot suivant (le natif appelle
 * TOUS les callbacks globaux, le buffer peut donc être écrit en doublon d'une
 * livraison déjà traitée par le listener).
 */
const MAX_AGE_MS = 2 * 60 * 1000;

declare global {
  interface Window {
    __kokenPendingPush?: BufferedPush;
    /**
     * Payload consommé par le chemin boot (main.tsx), conservé le temps que
     * le bridge soit prêt pour envoyer la télémétrie de clic.
     */
    __kokenBootPushForTracking?: Record<string, unknown>;
  }
}

export const clearPendingPush = (): void => {
  try {
    delete window.__kokenPendingPush;
  } catch {
    /* noop */
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
};

/**
 * Lit puis efface le buffer. Renvoie null si vide, périmé ou illisible.
 */
export const readAndClearPendingPush = (): Record<string, unknown> | null => {
  let buffered: BufferedPush | null = window.__kokenPendingPush ?? null;
  if (!buffered) {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      buffered = raw ? (JSON.parse(raw) as BufferedPush) : null;
    } catch {
      buffered = null;
    }
  }
  clearPendingPush();

  if (!buffered) return null;
  if (typeof buffered.at !== 'number' || Date.now() - buffered.at > MAX_AGE_MS) return null;
  return buffered.data && typeof buffered.data === 'object'
    ? (buffered.data as Record<string, unknown>)
    : null;
};

/**
 * Extrait un chemin interne sûr du payload push. Le deep-link in-app passe
 * par data.action_url relatif uniquement (cf. send-push-notification).
 *
 * ⚠️ Ne pas remplacer par un simple test startsWith : le parsing WHATWG
 * traite '\' comme '/' et strippe tab/CR/LF, donc '/\\evil.com' ou
 * '/\t/evil.com' passent un filtre « pas de // » mais se résolvent sur
 * https://evil.com (revue 03/08). On valide par résolution réelle contre
 * l'origine et on ne renvoie JAMAIS la chaîne brute.
 */
export const extractInternalActionUrl = (
  data: Record<string, unknown> | null | undefined,
): string | null => {
  const actionUrl = data?.action_url;
  if (typeof actionUrl !== 'string' || !actionUrl.startsWith('/')) return null;
  try {
    const resolved = new URL(actionUrl, window.location.origin);
    if (resolved.origin !== window.location.origin) return null;
    // « /.//hôte/x » se résout bien chez nous, mais son chemin devient
    // « //hôte/x ». Rendu tel quel, il serait relu comme une adresse sans
    // schéma : pushState le refuse, et le repli de navigateToActionUrl
    // CHARGEAIT cet autre site (constaté dans la coque iPhone le 10/10/2026).
    // Même règle que toInternalPath (internalActionPath.ts).
    const pathname = resolved.pathname.replace(/\/{2,}/g, '/');
    return pathname + resolved.search + resolved.hash;
  } catch {
    return null;
  }
};

/**
 * Chemin boot (main.tsx, avant createRoot) : si un tap a ouvert l'app à
 * froid et que le payload est déjà bufferisé, on réécrit l'URL pour que le
 * router monte directement sur la cible.
 */
export const consumePendingPushAtBoot = (): void => {
  const data = readAndClearPendingPush();
  const actionUrl = extractInternalActionUrl(data);
  if (!actionUrl) return;

  window.__kokenBootPushForTracking = data ?? undefined;
  if (window.location.pathname + window.location.search !== actionUrl) {
    window.history.replaceState(null, '', actionUrl);
  }
};
